import React from 'react';
import { generate } from 'shortid';
import { ImagePropsField } from '@src/slide/domain/ui-model/element-modeles/property/image-operation/constant/props-value';

export default class ImageFilter {
    private regularMarkId: string;
    public constructor() {
        this.regularMarkId = generate();
    }

    /**
     * 亮度对比度利用 filter 实现
     */
    public getBrightnessSaturateFilter(image) {
        if (!image) return;
        const { brightness, saturate } = image;
        return (brightness || (!isNaN(saturate) && saturate !== ImagePropsField.SATURATE.defaultValue)) && `brightness(${this.getBrightnessVal(brightness)}) saturate(${(saturate ?? 100) / 100})`;
    }

    // 计算对比度的值
    public getContrastValue = (val) => {
        const slope = val < 0 ? val + 1 : 1 + (val / 0.6);
        const intercept = (1 - slope) / 2;
        return [slope, intercept];
    };

    /**
     * 定义图片滤镜, 要将 id 绑定在 image 上，在 <def></def> 内使用
     * @param {*} index 第 index 的元素
     * @param {*} image 包括 sharpenSoften, brightness, contrast, saturate, colorTemperature 值
     */
    public defineImageFilter = (index, image) => {
        const { sharpenSoften, contrast, colorTemperature } = image;
        const id = this.getRegularMarkId();
        const filterId = `image-filter-${id}-${index}`;

        const imageFilters = (
            <filter id={filterId} filterUnits="objectBoundingBox" x="0%" y="0%" width="100%" height="100%">
                { this.renderTemperature(colorTemperature) }
                { this.renderContrast(contrast / 100) }
                { this.renderSharpenSoften(sharpenSoften)}
            </filter>
        );

        return { imageFilters, imageFiltersId: filterId };
    };

    public getImageFilter(image, filterOptions) {
        const { sharpenSoften, contrast, colorTemperature } = image;
        const { filterId } = filterOptions;
        const imageFilters = (
            <filter id={filterId} filterUnits="objectBoundingBox" x="0%" y="0%" width="100%" height="100%">
                { this.renderTemperature(colorTemperature) }
                { this.renderContrast(contrast / 100) }
                { this.renderSharpenSoften(sharpenSoften)}
            </filter>
        );
        return imageFilters;
    }

    public renderSharpenSoften(sharpenSoften) {
        if (sharpenSoften && sharpenSoften < 0) {
            return <feGaussianBlur stdDeviation={ 0.2 * Math.abs(sharpenSoften) }></feGaussianBlur>;
        }
        if (sharpenSoften && sharpenSoften > 0) {
            return <feConvolveMatrix order="3" kernelMatrix={this.getSharpenMatrix(sharpenSoften / 100)}></feConvolveMatrix>;
        }
        return null;
    }

    /**
     * 渲染对比度
     */
    public renderContrast = (contrast) => {
        let contrastSlope;
        let contrastIntercept;
        if (contrast) {
            [contrastSlope, contrastIntercept] = this.getContrastValue(contrast);
        }
        return contrast ? (
            <feComponentTransfer colorInterpolationFilters="sRGB">
                <feFuncR type="linear" slope={contrastSlope} intercept={contrastIntercept}></feFuncR>
                <feFuncG type="linear" slope={contrastSlope} intercept={contrastIntercept}></feFuncG>
                <feFuncB type="linear" slope={contrastSlope} intercept={contrastIntercept}></feFuncB>
            </feComponentTransfer>)
            : null;
    };

    /**
     * 渲染色调
     */
    public renderTemperature = (temperature) => {
        const temperatureFilters = (temperature !== undefined && temperature !== null
            && temperature !== ImagePropsField.COLORTEMPERATURE.defaultValue)
            ? <feColorMatrix in="SourceGraphic" type="matrix" values={this.getTemperatureMatrix(temperature)} colorInterpolationFilters="sRGB"></feColorMatrix>
            : null;
        return temperatureFilters;
    };

    // 计算亮度值
    public getBrightnessVal = (val) => {
        if (!val) return 1;
        if (val < 0) return (val / 100) + 1;
        return val !== 100 ? (1 / (1 - (val / 100))) : 100;
    };


    /**
     * 计算锐化矩阵
     */
    public getSharpenMatrix = (val) => {
        if (val <= 0) return;   // val < 0 表示模糊
        const realVal = (val * 16) + 1;
        return `0 ${-4 * val} 0 ${-4 * val} ${realVal} ${-4 * val} 0 ${-4 * val} 0`;
    };

    private getRegularMarkId = () => this.regularMarkId;

    /**
     * 获取 v 和 255 中较小的一个
     */
    private getRgbMin = v => Math.min(255, Math.max(0, Math.round(v)));

    /**
     * 计算色温的 rgba
     * @returns {object} 根据色温计算出的 red、green、blue，会根据此值生成色温矩阵
     * 参考：https://github.com/neilbartlett/color-temperature/blob/master/index.js
     */
    private getTemperatureRGB = (val) => {
        let temperature = val > 0 ? (5000 - (val / 5)) : (6500 + Math.abs(val));
        temperature = temperature / 100;

        let red = 255;
        let green = 255;
        let blue = 255;

        if (temperature < 65) {
            red = 255;
            green = temperature - 2;
            green = -115.25485562709179 - (0.44596950469579133 * green) + (104.49216199393888 * Math.log(green));
            blue = temperature - 10;
            blue = -204.76935184120902 + (0.8274096064007395 * blue) + (115.67994401066147 * Math.log(blue));
        } else if (temperature > 65) {
            red = temperature - 55;
            red = 351.97690566805693 + (0.114206453784165 * red) - (33.25366309332127 * Math.log(red));
            green = temperature - 50;
            green = 325.4494125711974 + (0.07943456536662342 * green) - (18.0852963507957 * Math.log(green));
            blue = 255;
        }

        return { red: this.getRgbMin(red) / 255, green: this.getRgbMin(green) / 255, blue: this.getRgbMin(blue) / 255 };
    };

    /**
     * 计算色温矩阵
     */
    private getTemperatureMatrix = (val) => {
        const { red, green, blue } = this.getTemperatureRGB(val - 6500);
        return `${red} 0 0 0 0 0 ${green} 0 0 0 0 0 ${blue} 0 0 0 0 0 1 0`;
    };
}
